import React, { useState, useEffect, useRef } from 'react'
import { CheckSquare, Square, Calendar, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

const API_URL = process.env.REACT_APP_BACKEND_URL

const ETAPES = [
  { key: 'photo', label: 'Photo' },
  { key: 'devis', label: 'Devis' },
  { key: 'demande_accord_pec', label: 'Demande accord PEC' },
  { key: 'pec_validee', label: 'Prise en charge validee' },
  { key: 'quittance_envoyee', label: 'Quittance envoyee' },
  { key: 'quittance_recue', label: 'Quittance recue' },
  { key: 'rib', label: 'RIB' },
  { key: 'demande_reglement', label: 'Demande de reglement' },
  { key: 'reglement_effectue', label: 'Reglement effectue' },
  { key: 'facture', label: 'Facture' },
]

const EMPTY_ETAPE = { fait: false, date: '', commentaire: '' }

export default function AvancementAmiableBlock({ sinistre, onRefresh }) {
  const [avancement, setAvancement] = useState({})
  const [saving, setSaving] = useState(false)
  const editingComment = useRef(null) // { key, value } pendant la saisie d'un commentaire
  const latest = useRef({})

  useEffect(() => {
    const data = sinistre?.avancement_amiable || {}
    const merged = {}
    ETAPES.forEach(e => {
      merged[e.key] = { ...EMPTY_ETAPE, ...(data[e.key] || {}) }
    })
    const ed = editingComment.current
    if (ed && merged[ed.key]) merged[ed.key] = { ...merged[ed.key], commentaire: ed.value }
    latest.current = merged
    setAvancement(merged)
  }, [sinistre?.id, sinistre?.avancement_amiable])

  async function persist(newAvancement) {
    setSaving(true)
    try {
      const r = await fetch(`${API_URL}/api/sinistres/${sinistre.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ avancement_amiable: newAvancement }),
      })
      if (!r.ok) throw new Error('Erreur serveur')
      onRefresh()
    } catch (e) {
      toast.error(e.message)
    } finally {
      setSaving(false)
    }
  }

  // Commentaire : enregistre en quittant le champ (blur / Entree), jamais pendant la frappe
  function commitComment(key) {
    const ed = editingComment.current
    editingComment.current = null
    if (!ed || ed.key !== key) return
    const serverVal = sinistre?.avancement_amiable?.[key]?.commentaire || ''
    if ((ed.value || '') === serverVal) return
    persist(latest.current)
  }

  function toggle(key) {
    setAvancement(prev => {
      const current = prev[key] || { ...EMPTY_ETAPE }
      const newFait = !current.fait
      const next = {
        ...prev,
        [key]: {
          ...current,
          fait: newFait,
          date: newFait && !current.date ? new Date().toISOString().slice(0, 10) : current.date,
        },
      }
      latest.current = next
      persist(next)
      return next
    })
  }

  function updateField(key, field, value) {
    setAvancement(prev => {
      const next = {
        ...prev,
        [key]: { ...(prev[key] || EMPTY_ETAPE), [field]: value },
      }
      latest.current = next
      if (field === 'commentaire') editingComment.current = { key, value }
      else persist(next)
      return next
    })
  }

  const nbFait = ETAPES.filter(e => avancement[e.key]?.fait).length
  const nbTotal = ETAPES.length
  const pctDone = Math.round((nbFait / nbTotal) * 100)

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="avancement-amiable-block">
      <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
        <h2 className="font-semibold text-slate-800 text-sm flex items-center gap-2">
          <CheckSquare size={16} className="text-indigo-500" />
          Avancement du dossier
          {saving && <Loader2 size={12} className="animate-spin text-indigo-400" />}
        </h2>
        <div className="flex items-center gap-2">
          <div className="w-20 h-1.5 bg-slate-200 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${pctDone === 100 ? 'bg-emerald-500' : pctDone >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
              style={{ width: `${pctDone}%` }}
            />
          </div>
          <span className="text-[10px] font-medium text-slate-500">{nbFait}/{nbTotal}</span>
        </div>
      </div>

      <div className="divide-y divide-slate-100">
        {ETAPES.map(etape => {
          const data = avancement[etape.key] || EMPTY_ETAPE
          const isFait = data.fait
          return (
            <div key={etape.key} className={`px-5 py-2.5 ${!isFait ? 'bg-red-50/40' : ''}`}
              data-testid={`avancement-${etape.key}`}>
              <div className="flex items-center gap-3">
                <button onClick={() => toggle(etape.key)} className="shrink-0"
                  data-testid={`avancement-${etape.key}-check`}>
                  {isFait
                    ? <CheckSquare size={16} className="text-emerald-600" />
                    : <Square size={16} className="text-red-400" />}
                </button>
                <span className={`text-xs font-medium flex-1 ${isFait ? 'text-slate-700' : 'text-red-600'}`}>
                  {etape.label}
                  {!isFait && <span className="text-[10px] font-normal text-red-400 ml-1">A fournir</span>}
                </span>
                <div className="flex items-center gap-1">
                  <Calendar size={10} className="text-slate-400" />
                  <input type="date" value={data.date || ''}
                    onChange={e => updateField(etape.key, 'date', e.target.value)}
                    className="text-[10px] border border-slate-200 rounded px-1.5 py-0.5 w-28 bg-white focus:ring-1 focus:ring-indigo-400"
                    data-testid={`avancement-${etape.key}-date`} />
                </div>
              </div>
              <div className="ml-[28px] mt-1">
                <input type="text" value={data.commentaire || ''}
                  onChange={e => updateField(etape.key, 'commentaire', e.target.value)}
                  onBlur={() => commitComment(etape.key)}
                  onKeyDown={e => { if (e.key === 'Enter') e.target.blur() }}
                  placeholder="Commentaire… (enregistré en quittant le champ)"
                  className="w-full text-[10px] text-slate-500 bg-transparent border-b border-transparent hover:border-slate-200 focus:border-indigo-300 focus:outline-none py-0.5"
                  data-testid={`avancement-${etape.key}-comment`} />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
